// src/playback.ts
var FINISH_TOLERANCE_MS = 250;
function playback(nowPlaying, nowMs) {
  const elapsed = nowMs - nowPlaying.startedAtMs;
  const positionMs = Math.min(Math.max(elapsed, 0), nowPlaying.durationMs);
  return {
    positionMs,
    remainingMs: nowPlaying.durationMs - positionMs,
    finished: elapsed >= nowPlaying.durationMs - FINISH_TOLERANCE_MS
  };
}
function mmss(ms) {
  const total = Math.max(0, Math.round(ms / 1e3));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}
function humanDuration(ms) {
  const minutes = Math.max(0, Math.round(ms / 6e4));
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, "0")}`;
}

// src/queue.ts
var isPlayable = (track) => track.state === "queued";
var IN_QUEUE = /* @__PURE__ */ new Set(["queued", "importing"]);
function sortedQueue(tracks) {
  return tracks.filter((t) => IN_QUEUE.has(t.state)).sort((a, b) => a.queuePos - b.queuePos || a.id.localeCompare(b.id));
}
function nextPlayable(tracks) {
  return sortedQueue(tracks).find((t) => t.state === "queued") ?? null;
}
function tailQueuePos(tracks) {
  if (tracks.length === 0) return 0;
  return Math.max(...tracks.map((t) => t.queuePos)) + 1;
}
function batchQueuePositions(tracks, count) {
  if (count <= 0) return [];
  const start = tailQueuePos(tracks);
  return Array.from({ length: count }, (_, i) => start + i);
}
function headQueuePos(tracks) {
  const first = sortedQueue(tracks)[0];
  return first ? first.queuePos - 1 : 0;
}
function canRemoveFromQueue(track, uid) {
  if (!track) return { ok: false, reason: "gone" };
  if (track.addedByUid !== uid) return { ok: false, reason: "not-yours" };
  if (track.state === "playing") return { ok: false, reason: "on-air" };
  if (!IN_QUEUE.has(track.state)) return { ok: false, reason: "already-played" };
  return { ok: true };
}

// src/rerun.ts
var RERUNNABLE_KINDS = ["youtube", "music"];
var RERUN_POOL_SIZE = 100;
var canRerun = (track) => track.state === "played" && RERUNNABLE_KINDS.includes(track.kind) && track.durationMs > 0;
function rerunPool({ liked, latest, size = RERUN_POOL_SIZE }) {
  const byId = /* @__PURE__ */ new Map();
  for (const track of [...liked, ...latest]) {
    if (byId.size >= size) break;
    if (!byId.has(track.id)) byId.set(track.id, track);
  }
  return [...byId.values()];
}
function pickRerun({ pool, excludeId = null, random = Math.random }) {
  const eligible = pool.filter((t) => t.id !== excludeId && canRerun(t));
  if (eligible.length === 0) return null;
  const index = Math.min(eligible.length - 1, Math.floor(random() * eligible.length));
  return eligible[index] ?? null;
}
var creditedTo = (track) => track.rerun ? null : track.addedByName;

// src/advance.ts
var ADVANCE_GRACE_MS = 1500;
function decideAdvance({
  state,
  queue,
  nowMs,
  expectedTrackId,
  force = false,
  rerunPool: rerunPool2 = [],
  random
}) {
  const current = state.nowPlaying;
  if ((current?.trackId ?? null) !== expectedTrackId) {
    return { kind: "noop", reason: "stale-expectation" };
  }
  if (current && !force) {
    const elapsed = nowMs - current.startedAtMs;
    if (elapsed < current.durationMs - ADVANCE_GRACE_MS) {
      return { kind: "noop", reason: "not-finished" };
    }
  }
  const finishedTrackId = current?.trackId ?? null;
  const next = nextPlayable(queue);
  if (next) {
    return {
      kind: "play",
      finishedTrackId,
      nowPlaying: {
        trackId: next.id,
        // Un morceau coupé par une dédicace reprend là où il en était : on recule
        // simplement son startedAt du temps déjà écouté.
        startedAtMs: nowMs - (next.resumeOffsetMs ?? 0),
        durationMs: next.durationMs
      }
    };
  }
  const rerun = pickRerun({ pool: rerunPool2, excludeId: finishedTrackId, random });
  if (rerun) {
    return {
      kind: "play",
      finishedTrackId,
      // une rediffusion repart du début : son offset de reprise a été consommé
      // la fois précédente
      nowPlaying: { trackId: rerun.id, startedAtMs: nowMs, durationMs: rerun.durationMs, rerun: true }
    };
  }
  return current ? { kind: "stop", finishedTrackId: current.trackId } : { kind: "noop", reason: "nothing-to-play" };
}

// src/mic.ts
function planMicTakeover({ state, current, queue, nowMs, clip }) {
  const head = headQueuePos(queue);
  if (current?.kind === "voice" && state.nowPlaying?.trackId === current.id) {
    return { kind: "enqueue-front", clipQueuePos: head };
  }
  const playing = state.nowPlaying;
  const interruptedPos = head;
  const clipQueuePos = head - 1;
  if (!playing || !current) {
    return {
      kind: "takeover",
      clipQueuePos,
      interrupted: null,
      nowPlaying: { trackId: clip.id, startedAtMs: nowMs, durationMs: clip.durationMs },
      finishedTrackId: null
    };
  }
  const { positionMs, finished } = playback(playing, nowMs);
  return {
    kind: "takeover",
    clipQueuePos,
    // Un morceau déjà terminé n'a rien à reprendre : il part à l'historique.
    interrupted: finished ? null : { trackId: playing.trackId, queuePos: interruptedPos, resumeOffsetMs: positionMs },
    nowPlaying: { trackId: clip.id, startedAtMs: nowMs, durationMs: clip.durationMs },
    finishedTrackId: finished ? playing.trackId : null
  };
}

// src/skip.ts
function skipThreshold(listenerCount) {
  return Math.max(1, Math.ceil(Math.sqrt(listenerCount)));
}
function describeVoters(skipVotes, listeners) {
  const uids = new Set(Object.keys(skipVotes ?? {}));
  if (uids.size === 0) return [];
  const known = listeners.filter((l) => uids.has(l.uid)).map((l) => ({ uid: l.uid, name: l.name, photo: l.photo }));
  const anonymous = [...uids].filter((uid) => !known.some((k) => k.uid === uid)).map((uid) => ({ uid, name: "quelqu'un" }));
  return [...known, ...anonymous];
}
function skipOutcome({ votes, listenerCount }) {
  const unique = new Set(votes).size;
  const needed = skipThreshold(listenerCount);
  return unique >= needed ? { kind: "skip" } : { kind: "pending", votes: unique, needed };
}

// src/like.ts
function likeCount(likes) {
  return Object.keys(likes ?? {}).length;
}
function hasLiked(likes, uid) {
  return likes?.[uid] === true;
}
function decideLike(likes, uid) {
  const count = likeCount(likes);
  return hasLiked(likes, uid) ? { kind: "unlike", count: count - 1 } : { kind: "like", count: count + 1 };
}

// src/clock.ts
var RESYNC_DRIFT_MS = 1500;
function clockOffsetMs(sentAtMs, serverMs, receivedAtMs) {
  return serverMs - (sentAtMs + receivedAtMs) / 2;
}
function needsResync(driftMs) {
  return Math.abs(driftMs) > RESYNC_DRIFT_MS;
}

// src/presence.ts
var PRESENCE_TTL_MS = 45e3;
var HEARTBEAT_INTERVAL_MS = 2e4;
function activeListeners(listeners, nowMs) {
  return listeners.filter((l) => nowMs - l.lastSeenAtMs < PRESENCE_TTL_MS);
}

// src/youtube.ts
var VIDEO_ID = /^[\w-]{11}$/;
var asUrl = (input) => {
  const trimmed = input.trim();
  if (!trimmed) return null;
  try {
    return new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
  } catch {
    return null;
  }
};
function parseYouTubeVideoId(input) {
  const url = asUrl(input);
  if (!url) return null;
  const host = url.hostname.replace(/^www\./, "").toLowerCase();
  const path = url.pathname.replace(/^\/+/, "");
  const candidate = host === "youtu.be" ? path.split("/")[0] : host === "youtube.com" || host === "music.youtube.com" || host === "m.youtube.com" ? path.startsWith("shorts/") || path.startsWith("embed/") || path.startsWith("v/") ? path.split("/")[1] : url.searchParams.get("v") ?? void 0 : void 0;
  return candidate && VIDEO_ID.test(candidate) ? candidate : null;
}
function parseIsoDuration(iso) {
  const m = /^P(?:\d+D)?T?(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/.exec(iso.trim());
  if (!m) return 0;
  const [, h, min, s] = m;
  const seconds = Number(h ?? 0) * 3600 + Number(min ?? 0) * 60 + Number(s ?? 0);
  return seconds * 1e3;
}
var isVideoId = (candidate) => typeof candidate === "string" && VIDEO_ID.test(candidate);
function looksLikeUrl(input) {
  const trimmed = input.trim();
  if (!trimmed || /\s/.test(trimmed)) return false;
  if (/^https?:\/\//i.test(trimmed)) return true;
  const url = asUrl(trimmed);
  if (!url) return false;
  return /\.[a-z]{2,}$/i.test(url.hostname);
}
var PLAYLIST_ID = /^(?:PL|UU|OL)[\w-]{10,}$/;
function parseYouTubePlaylistId(input) {
  const url = asUrl(input);
  if (!url) return null;
  const host = url.hostname.replace(/^www\./, "").toLowerCase();
  if (host !== "youtube.com" && host !== "music.youtube.com" && host !== "m.youtube.com") {
    return null;
  }
  const list = url.searchParams.get("list");
  return list && PLAYLIST_ID.test(list) ? list : null;
}

// src/youtube-search.ts
var LIVE_REJECTED = /* @__PURE__ */ new Set(["is_live", "is_upcoming", "post_live"]);
var AVAILABILITY_ALLOWED = /* @__PURE__ */ new Set(["public", "unlisted"]);
function bestThumb(thumbnails) {
  if (!Array.isArray(thumbnails)) return void 0;
  let best;
  for (const t of thumbnails) {
    const url = t?.url;
    if (typeof url !== "string" || !url) continue;
    const w = Number(t.width) || 0;
    const h = Number(t.height) || 0;
    const area = w * h;
    if (!best || area > best.area) best = { url, area };
  }
  return best?.url;
}
function parseSearchEntries(payload, opts) {
  const entries = payload?.entries;
  if (!Array.isArray(entries)) return [];
  const results = [];
  for (const raw of entries) {
    const entry = raw;
    const videoId = entry?.id;
    if (!isVideoId(videoId)) continue;
    const title = typeof entry.title === "string" ? entry.title.trim() : "";
    if (!title) continue;
    const live = entry.live_status;
    if (typeof live === "string" && LIVE_REJECTED.has(live)) continue;
    const availability = entry.availability;
    if (typeof availability === "string" && !AVAILABILITY_ALLOWED.has(availability)) continue;
    const seconds = typeof entry.duration === "number" ? entry.duration : Number.NaN;
    if (!Number.isFinite(seconds) || seconds <= 0) continue;
    const durationMs = Math.round(seconds * 1e3);
    if (durationMs > opts.maxDurationMs) continue;
    const uploader = [entry.channel, entry.uploader].find(
      (v) => typeof v === "string" && v.trim().length > 0
    );
    const thumbUrl = bestThumb(entry.thumbnails);
    results.push({
      videoId,
      title,
      ...uploader ? { uploader: uploader.trim() } : {},
      ...thumbUrl ? { thumbUrl } : {},
      durationMs
    });
  }
  return results;
}
var MAX_QUERY_LENGTH = 120;
function normalizeSearchQuery(raw) {
  return raw.replace(/\s+/g, " ").trim().slice(0, MAX_QUERY_LENGTH);
}
var searchCacheKey = (raw) => normalizeSearchQuery(raw).toLowerCase();
function parsePlaylist(payload, opts) {
  const envelope = payload;
  const title = typeof envelope?.title === "string" ? envelope.title.trim() : "";
  const count = envelope?.playlist_count;
  return {
    ...title ? { title } : {},
    ...typeof count === "number" && count > 0 ? { totalCount: count } : {},
    results: parseSearchEntries(payload, opts)
  };
}
function parsePlaylistSearchResults(payload) {
  const entries = payload?.entries;
  if (!Array.isArray(entries)) return [];
  const summaries = [];
  for (const raw of entries) {
    const entry = raw;
    if (entry?.ie_key !== "YoutubeTab") continue;
    if (typeof entry.id !== "string") continue;
    const playlistId = parseYouTubePlaylistId(`https://www.youtube.com/playlist?list=${entry.id}`);
    if (!playlistId) continue;
    const title = typeof entry.title === "string" ? entry.title.trim() : "";
    if (!title) continue;
    const uploader = [entry.channel, entry.uploader].find(
      (v) => typeof v === "string" && v.trim().length > 0
    );
    const thumbUrl = bestThumb(entry.thumbnails);
    summaries.push({
      playlistId,
      title,
      ...uploader ? { uploader: uploader.trim() } : {},
      ...thumbUrl ? { thumbUrl } : {}
    });
  }
  return summaries;
}

// src/chat.ts
var MESSAGE_MAX_LENGTH = 500;
var MESSAGE_MIN_INTERVAL_MS = 700;
var CONTROL_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;
function normalizeMessageText(raw) {
  const cleaned = raw.replace(CONTROL_CHARS, "").replace(/\r\n?/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
  if (!cleaned) return null;
  return cleaned.slice(0, MESSAGE_MAX_LENGTH);
}
function isRateLimited(lastSentAtMs, nowMs) {
  if (lastSentAtMs === void 0) return false;
  return nowMs - lastSentAtMs < MESSAGE_MIN_INTERVAL_MS;
}
function isSameDay(aMs, bMs) {
  const a = new Date(aMs);
  const b = new Date(bMs);
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function dayLabel(ms, nowMs) {
  if (isSameDay(ms, nowMs)) return "Aujourd'hui";
  const yesterday = new Date(nowMs);
  yesterday.setDate(yesterday.getDate() - 1);
  if (isSameDay(ms, yesterday.getTime())) return "Hier";
  const day = new Date(ms);
  const options = { weekday: "long", day: "numeric", month: "long" };
  if (day.getFullYear() !== new Date(nowMs).getFullYear()) options.year = "numeric";
  return day.toLocaleDateString("fr-FR", options);
}
function msUntilNextLocalDay(nowMs) {
  const next = new Date(nowMs);
  next.setHours(24, 0, 0, 0);
  return next.getTime() - nowMs;
}
export {
  ADVANCE_GRACE_MS,
  FINISH_TOLERANCE_MS,
  HEARTBEAT_INTERVAL_MS,
  MAX_QUERY_LENGTH,
  MESSAGE_MAX_LENGTH,
  MESSAGE_MIN_INTERVAL_MS,
  PRESENCE_TTL_MS,
  RERUNNABLE_KINDS,
  RERUN_POOL_SIZE,
  RESYNC_DRIFT_MS,
  activeListeners,
  batchQueuePositions,
  canRemoveFromQueue,
  canRerun,
  clockOffsetMs,
  creditedTo,
  dayLabel,
  decideAdvance,
  decideLike,
  describeVoters,
  hasLiked,
  headQueuePos,
  humanDuration,
  isPlayable,
  isRateLimited,
  isSameDay,
  isVideoId,
  likeCount,
  looksLikeUrl,
  mmss,
  msUntilNextLocalDay,
  needsResync,
  nextPlayable,
  normalizeMessageText,
  normalizeSearchQuery,
  parseIsoDuration,
  parsePlaylist,
  parsePlaylistSearchResults,
  parseSearchEntries,
  parseYouTubePlaylistId,
  parseYouTubeVideoId,
  pickRerun,
  planMicTakeover,
  playback,
  rerunPool,
  searchCacheKey,
  skipOutcome,
  skipThreshold,
  sortedQueue,
  tailQueuePos
};
